/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2024.
 */

import { B2B } from '@/components/blocks/B2B';
import { FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { Linkable } from '@/components/blocks/Linkable';
import { RegisterBenefits } from '@/components/content/Login/parts/RegisterBenefits';
import { LoginDrawerSx, LoginSx } from '@/components/content/Login/styles/container';
import { useSessionDialog } from '@/data/Content/SessionDialog';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { useRecurringOrderState } from '@/data/state/useRecurringOrderState';
import { logEvent } from '@/data/utils/logEventsUtils';
import { mergeSx } from '@/utils/mergeSx';
import { useMobileApp } from '@/utils/useMobileApp';
import { Divider, Stack, Typography } from '@mui/material';
import { CHECKOUT } from 'shared/custom/constants/commonConstants';

export const RegisterOrCheckout: React.FC<{
	drawerVersion?: boolean;
	closeDrawer?: () => void;
}> = ({ drawerVersion = false, closeDrawer }) => {
	const signInNLS = useLocalization('SignIn');
	const router = useNextRouter();
	const checkoutFlow = router.query.flow === CHECKOUT;
	const { isMobileApp } = useMobileApp();
	const {
		recurringOrderInfo: { isRecurring },
	} = useRecurringOrderState();

	const noAccountMsg = signInNLS.noAccount.t();
	const noAccountButtonText = checkoutFlow
		? signInNLS.CheckoutAsGuestButton.t()
		: signInNLS.registerNow.t();

	const { openSessionDialog, sessionDialogOpen } = useSessionDialog();
	const onRegisterClick = (e: React.MouseEvent) => {
		if (isMobileApp) {
			e.preventDefault();
			logEvent('onRegisterClick');
		}
		if (drawerVersion && closeDrawer) {
			closeDrawer();
		}
	};

	const onRegisterDialogClick = (e: React.MouseEvent) => {
		if (isMobileApp) {
			e.preventDefault();
			logEvent('onRegisterClick');
			closeDrawer?.();
			return;
		}
		openSessionDialog('register');
	};

	return checkoutFlow && !isRecurring ? (
		<FlowIfEnabled feature={EMS_STORE_FEATURE.GUEST_SHOPPING}>
			<Divider />
			<Stack alignItems="center" spacing={2}>
				<Typography variant="bodyBaseline" sx={LoginSx.noAccountLabel}>
					{noAccountMsg}
				</Typography>
				<Linkable
					type="button"
					variant="outlined"
					sx={LoginSx.button}
					data-testid="button-sign-in-register"
					id="button-sign-in-register"
					onClick={onRegisterDialogClick}
					fullWidth
				>
					{noAccountButtonText}
				</Linkable>
			</Stack>
		</FlowIfEnabled>
	) : !checkoutFlow ? (
		<B2B is={false}>
			<Divider sx={mergeSx(LoginSx.divider, drawerVersion ? LoginDrawerSx.divider : {})} />
			<Stack>
				<Typography
					variant="bodyBaseline"
					sx={mergeSx(LoginSx.noAccountLabel, drawerVersion ? LoginDrawerSx.noAccountLabel : {})}
				>
					{noAccountMsg}
				</Typography>
				<RegisterBenefits />
				{sessionDialogOpen ? (
					<Linkable
						type="button"
						variant="outlined"
						sx={LoginSx.buttonOutlined}
						data-testid="button-sign-in-register"
						id="button-sign-in-register"
						onClick={onRegisterDialogClick}
						fullWidth
					>
						{noAccountButtonText}
					</Linkable>
				) : (
					<Linkable
						type="button"
						variant="outlined"
						sx={LoginSx.buttonOutlined}
						data-testid="button-sign-in-register"
						id={drawerVersion ? 'cartRegisterButton' : 'button-sign-in-register'}
						href={'signup'}
						onClick={onRegisterClick}
						fullWidth
					>
						{drawerVersion ? signInNLS.SignUp.t() : noAccountButtonText}
					</Linkable>
				)}
			</Stack>
		</B2B>
	) : null;
};
